import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { EmptyState } from "@/components/PageHeader";
import {
  BookmarkPlus, Link2, ChefHat, Loader2, Trash2, Play,
  ListPlus, ExternalLink, Clock, Check, ShoppingBag, ChevronDown, ChevronUp, Youtube,
} from "lucide-react";
import { toast } from "sonner";
import LittleDetour from "@/components/LittleDetour";

// Format an ISO date as "3 Feb" (short AU-friendly)
function fmtShortDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
  } catch { return ""; }
}

function IngredientChips({ have = [], missing = [] }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="ingredient-chips">
      {have.map((h) => (
        <span key={`h-${h}`} className="chip ft-chip-have inline-flex items-center gap-1">
          <Check size={10} /> {h}
        </span>
      ))}
      {missing.map((m) => (
        <span key={`m-${m}`} className="chip ft-chip-missing inline-flex items-center gap-1">
          <ShoppingBag size={10} /> {m}
        </span>
      ))}
    </div>
  );
}

function YouTubeEmbed({ id, sourceUrl }) {
  if (!id) return null;
  return (
    <div className="recipe-polaroid__media mt-4">
      <div
        className="relative w-full rounded-xl overflow-hidden"
        style={{
          aspectRatio: "16 / 9",
          boxShadow: "inset 0 0 0 1px rgba(60,45,30,0.15), 0 8px 22px -14px rgba(60,45,30,0.35)",
          background: "#000",
        }}
      >
        <iframe
          data-testid={`youtube-embed-${id}`}
          title="Recipe video"
          src={`https://www.youtube.com/embed/${id}?rel=0`}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="absolute inset-0 w-full h-full"
        />
      </div>
      {/* Fallback: some uploaders disable embedding — a direct link always works */}
      <a
        href={sourceUrl || `https://www.youtube.com/watch?v=${id}`}
        target="_blank"
        rel="noreferrer"
        data-testid={`youtube-external-${id}`}
        className="btn-forest-ghost mt-2 inline-flex items-center gap-1.5 text-xs w-full justify-center"
      >
        <Youtube size={13} /> Open on YouTube if the player is quiet
      </a>
    </div>
  );
}

function RecipeCard({ recipe, member, onDelete, onAddMissing }) {
  const [expanded, setExpanded] = useState(false);
  const missingCount = recipe.missing?.length || 0;
  const haveCount = recipe.have?.length || 0;
  const bylineName = member?.name || member?.email?.split("@")[0] || "the family";
  const bylineDate = fmtShortDate(recipe.created_at);

  return (
    <article
      data-testid={`saved-recipe-${recipe.id}`}
      className="recipe-polaroid flex flex-col"
    >
      <span className="recipe-polaroid__tape" aria-hidden />

      <div className="flex items-start justify-between gap-3 mb-3 pt-1">
        <div className="w-9 h-9 rounded-lg grid place-items-center shrink-0" style={{ background: "rgba(46,72,52,0.10)", color: "var(--forest)" }}>
          <ChefHat size={17} strokeWidth={1.6} />
        </div>
        <button
          data-testid={`saved-recipe-delete-${recipe.id}`}
          onClick={() => onDelete(recipe)}
          className="p-2 rounded-full text-hearth-mute hover:text-hearth-coral hover:bg-hearth-coral/10 transition"
          title="Remove from The Family Table"
        >
          <Trash2 size={14} />
        </button>
      </div>

      <h3 className="recipe-polaroid__caption">{recipe.title}</h3>
      <div className="recipe-polaroid__meta mt-2 flex items-center gap-3 flex-wrap">
        {recipe.cuisine && <span>{recipe.cuisine}</span>}
        {recipe.time_min != null && <span className="inline-flex items-center gap-1"><Clock size={10}/> ~{recipe.time_min} min</span>}
        {recipe.difficulty && <span>{recipe.difficulty}</span>}
      </div>

      {recipe.blurb && (
        <p className="text-sm text-hearth-mute mt-3 italic leading-relaxed">{recipe.blurb}</p>
      )}

      {recipe.warm_message && (
        <div
          className="mt-4 recipe-polaroid__whisper"
          data-testid={`warm-message-${recipe.id}`}
        >
          {recipe.warm_message}
        </div>
      )}

      {recipe.is_youtube && recipe.youtube_id && <YouTubeEmbed id={recipe.youtube_id} sourceUrl={recipe.source_url} />}

      <div className="mt-5">
        <div className="flex items-center justify-between mb-2">
          <span className="recipe-polaroid__meta">
            <span className="font-mono">{haveCount}</span> in the pantry ·{" "}
            <span className="font-mono">{missingCount}</span> to gather
          </span>
          {recipe.source_url && (
            <a
              href={recipe.source_url}
              target="_blank"
              rel="noreferrer"
              className="text-[11px] tracking-widest uppercase inline-flex items-center gap-1 transition"
              style={{ color: "var(--forest)" }}
              data-testid={`saved-recipe-source-${recipe.id}`}
            >
              <ExternalLink size={11} /> source
            </a>
          )}
        </div>
        <IngredientChips have={recipe.have || []} missing={recipe.missing || []} />
      </div>

      {recipe.steps?.length > 0 && (
        <>
          <button
            data-testid={`saved-recipe-toggle-steps-${recipe.id}`}
            onClick={() => setExpanded((x) => !x)}
            className="mt-4 text-xs inline-flex items-center gap-1 transition self-start"
            style={{ color: "var(--forest)" }}
          >
            {expanded ? <>Fold away <ChevronUp size={13}/></> : <>Read the method <ChevronDown size={13}/></>}
          </button>
          {expanded && (
            <ol className="mt-3 space-y-2 text-sm">
              {recipe.steps.map((step, sidx) => (
                <li key={sidx} className="flex gap-2">
                  <span
                    className="w-5 h-5 rounded-full text-[11px] font-mono grid place-items-center shrink-0 mt-0.5"
                    style={{ background: "rgba(176,138,72,0.18)", color: "var(--forest-ink)" }}
                  >
                    {sidx + 1}
                  </span>
                  <span className="leading-relaxed" style={{ color: "var(--forest-ink)" }}>{step}</span>
                </li>
              ))}
            </ol>
          )}
        </>
      )}

      <div className="mt-6 pt-4 flex items-center justify-end" style={{ borderTop: "1px dashed rgba(60,45,30,0.15)" }}>
        <button
          data-testid={`saved-recipe-add-missing-${recipe.id}`}
          onClick={() => onAddMissing(recipe)}
          disabled={missingCount === 0}
          className="btn-forest text-sm inline-flex items-center"
        >
          <ListPlus size={13} className="mr-2" />
          {missingCount
            ? `Gather ${missingCount} to shop`
            : "Nothing to gather — ready to cook"}
        </button>
      </div>

      {/* Handwritten byline — a scrap of paper at the corner of the polaroid */}
      <div className="mt-4 flex justify-start" data-testid={`saved-recipe-byline-${recipe.id}`}>
        <span className="recipe-polaroid__byline">
          added by {bylineName}
          {bylineDate ? `, ${bylineDate}` : ""}
        </span>
      </div>
    </article>
  );
}

export default function FamilyTable() {
  const [url, setUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const [recipes, setRecipes] = useState([]);
  const [members, setMembers] = useState([]);

  const load = useCallback(async () => {
    try {
      const [r, m] = await Promise.all([
        api.get("/saved-recipes"),
        api.get("/household/members"),
      ]);
      setRecipes(r.data);
      setMembers(m.data.members || []);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const memberFor = (id) => members.find((m) => m.id === id);

  const importRecipe = async () => {
    const clean = url.trim();
    if (!clean) return;
    setImporting(true);
    try {
      const { data } = await api.post("/recipes/import", { url: clean });
      setUrl("");
      toast.success(`Set at the table: ${data.title}`);
      setRecipes((prev) => [data, ...prev.filter((r) => r.id !== data.id)]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't read that link");
    } finally {
      setImporting(false);
    }
  };

  const onDelete = async (recipe) => {
    try {
      await api.delete(`/saved-recipes/${recipe.id}`);
      setRecipes((prev) => prev.filter((r) => r.id !== recipe.id));
      toast.success("Cleared from the table");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const onAddMissing = async (recipe) => {
    const missing = recipe.missing || [];
    if (!missing.length) return;
    try {
      const { data } = await api.post("/recipes/add-missing", { ingredients: missing });
      toast.success(`Added ${data.count} to the shopping list`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="family-table">
      <div className="family-table__page" data-testid="family-table-page">
        {/* Header */}
        <header className="mb-8">
          <p className="family-table__eyebrow" data-testid="family-table-eyebrow">
            The Family Table
          </p>
          <div className="flex flex-wrap items-start justify-between gap-4 mt-2">
            <h1 className="family-table__title text-4xl md:text-5xl lg:text-6xl" data-testid="family-table-title">
              Recipes worth passing down.
            </h1>
            <div className="pt-2">
              <LittleDetour variant="quiet" />
            </div>
          </div>
          <p className="family-table__lede mt-3 max-w-2xl text-lg md:text-xl">
            Bring a favourite video, an old blog, or a chef&apos;s note to the table.
            We&apos;ll set the ingredients beside what&apos;s already in your pantry —
            so the only question left is what wine you&apos;re opening.
          </p>
        </header>

        {/* Import envelope */}
        <section className="envelope-panel mb-8" data-testid="import-recipe-card">
          <div className="flex items-baseline justify-between gap-3 mb-3 flex-wrap">
            <div className="inline-flex items-center gap-2">
              <BookmarkPlus size={18} style={{ color: "var(--forest)" }} />
              <span className="envelope-panel__title">Add a recipe to the table</span>
            </div>
            <span className="recipe-polaroid__meta">YouTube · blog · print-out URL</span>
          </div>
          <div className="flex gap-2 items-center flex-wrap sm:flex-nowrap">
            <div className="flex-1 min-w-0 relative">
              <Link2 size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: "var(--forest-soft)" }} />
              <Input
                data-testid="import-recipe-url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !importing) { e.preventDefault(); importRecipe(); } }}
                disabled={importing}
                placeholder="Paste a YouTube link or a recipe URL…"
                className="envelope-input w-full"
              />
            </div>
            <button
              data-testid="import-recipe-submit"
              onClick={importRecipe}
              disabled={importing || !url.trim()}
              className="btn-forest inline-flex items-center whitespace-nowrap"
            >
              {importing ? <Loader2 size={14} className="mr-2 animate-spin"/> : <Play size={13} className="mr-2"/>}
              {importing ? "Reading the page…" : "Set the table"}
            </button>
          </div>
          <p className="text-xs mt-3 italic" style={{ color: "var(--forest-soft)" }}>
            Videos are embedded so you can watch and cook at once. Blog pages
            are read carefully — only the ingredients that actually appear are kept.
          </p>
        </section>

        {recipes.length === 0 ? (
          <div className="pt-4">
            <EmptyState
              icon={ChefHat}
              title="The table is set, quietly waiting."
              message="Paste a favourite recipe above and we'll lay the first place setting."
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="saved-recipes-grid">
            {recipes.map((r) => (
              <RecipeCard
                key={r.id}
                recipe={r}
                member={memberFor(r.created_by)}
                onDelete={onDelete}
                onAddMissing={onAddMissing}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
